/**
 * WhatsApp Constants
 * Defines all WhatsApp-related constants used throughout the application
 */

/**
 * WhatsApp Connection Status Enum
 * Defines all possible connection status values
 */

export const ServerConnectionStatus = {
    CHECKING: 'CHECKING',
    CONNECTED: 'CONNECTED',
    NEEDS_RECONNECT: 'NEEDS_RECONNECT',
    NEEDS_CONFIGURATION: 'NEEDS_CONFIGURATION',
    NOT_CONNECTED: 'NOT_CONNECTED',
    PENDING: 'PENDING',
    UNKNOWN: 'UNKNOWN'
};

export const WhatsAppConnectionStatus = (serverStatus) => {
    switch (serverStatus) {
        case 'CHECKING':
            return ServerConnectionStatus.CHECKING;
        case 'CONNECTED':
            return ServerConnectionStatus.CONNECTED;
        case 'NO_TOKEN':
        case 'TOKEN_CORRUPT':
            return ServerConnectionStatus.NEEDS_RECONNECT;
        case 'PENDING':
            return ServerConnectionStatus.PENDING;
        case 'NO_CONFIGURATION':
        case 'INCOMPLETE_CONFIGURATION':
            return ServerConnectionStatus.NEEDS_CONFIGURATION;
        case 'NOT_CONNECTED':
            return ServerConnectionStatus.NOT_CONNECTED;
        default:
            return ServerConnectionStatus.UNKNOWN;
    }
  };

  export const WhatsAppFullyConnected = (serverStatus) => {
    return WhatsAppConnectionStatus(serverStatus) === ServerConnectionStatus.CONNECTED && 
    serverStatus.configurationExists &&
    serverStatus.connected;
  };

  export const GetOAuthURL = (serverStatus) => {
    return serverStatus.connectUrl;
  }
  
  /**
   * WhatsApp OAuth Status Enum
   * Defines OAuth connection status values
   */
  export const WhatsAppOAuthStatus = {
    CONNECTED: 'connected',
    NOT_CONNECTED: 'notconnected'
  };
  
  /**
   * WhatsApp Tab IDs
   * Defines the tab identifiers for segmented navigation
   */
  export const WhatsAppTabIds = {
    //LEADS_SETUP: 'leads-setup',
    WHATSAPP: 'whatsapp',
    TEMPLATES: 'templates',
    GROUP: 'groups',
    BROADCAST: 'broadcast'
  };
  
  /**
   * WhatsApp Loading States
   * Defines the different loading states for async operations
   */
  export const WhatsAppLoadingStates = {
    CONNECTING: 'connecting',
    GENERATING: 'generating',
    TESTING: 'testing'
  };
  
  /**
   * WhatsApp Text Constants
   * All user-facing text strings for WhatsApp integration
   */
  export const WhatsAppTexts = {
    // Page Titles and Descriptions
    PAGE_TITLE: 'WhatsApp Business Integration',
    PAGE_SUBTITLE: 'Manage your WhatsApp leads and business communications.',
    
    // Leads Setup Section
    LEADS_SETUP_TITLE: 'WhatsApp Leads Setup',
    WHATSAPP_TAB_TITLE: 'Dashboard',
    LEADS_SETUP_SUBTITLE: 'Connect your WhatsApp Business account to start receiving leads automatically.',
    
    // Connected State
    CONNECTED_TITLE: 'You are all set!',
    CONNECTED_DESCRIPTION: 'Your WhatsApp Business account is connected and ready to receive leads.',
    
    // Setup Cards
    CONNECT_WHATSAPP_DESCRIPTION: 'Authorize access to your Business account.',
    CONNECT_BUTTON_TEXT: 'Connect',
    
    GENERATE_WEBHOOK_TITLE: 'Generate Webhook',
    GENERATE_WEBHOOK_DESCRIPTION: 'Create secure webhook credentials for your integration.',
    GENERATE_WEBHOOK_BUTTON_TEXT: 'Generate Webhook',
    
    // Webhook Section
    WEBHOOK_CONFIG_TITLE: 'Webhook Configuration',
    WEBHOOK_CONFIG_SUBTITLE: 'Copy these credentials to your WhatsApp Business Manager',
    WEBHOOK_URL_LABEL: 'Webhook URL:',
    VERIFY_TOKEN_LABEL: 'Verify Token:',
    
    // Test Setup Section
    TEST_SETUP_TITLE: 'Test Setup',
    TEST_SETUP_DESCRIPTION: 'Send a test message to verify your integration is working',
    TEST_SETUP_INFO_TEXT: 'This will send a test message to your WhatsApp Business number to verify the webhook integration is working correctly.',
    SEND_TEST_BUTTON_TEXT: 'Send Test Message & Verify Setup',
    
    // WhatsApp Business Manager Instructions
    BUSINESS_MANAGER_TITLE: 'WhatsApp Business Manager Setup',
    BUSINESS_MANAGER_SUBTITLE: 'Follow these steps in your WhatsApp Business Manager:',
    
    // Loading States
    CONNECTING_TEXT: 'Connecting...',
    GENERATING_TEXT: 'Generating...',
    SENDING_TEXT: 'Sending...',
    CHECKING_STATUS_TEXT: 'Checking WhatsApp connection status...',
    
    // WhatsApp Tab
    WHATSAPP_DASHBOARD_TAB_TITLE: 'Dashboard',
    WHATSAPP_TAB_SUBTITLE: 'Manage your WhatsApp conversations and settings.',
    WHATSAPP_TAB_PLACEHOLDER: 'WhatsApp management features coming soon...',

    SEND_TEST_MESSAGE_BUTTON_TEXT: 'Send Test Message & Verify Setup',

    CONFIRM_WEBHOOK_UPDATED_TITLE: '* Once done, please click the button below to confirm the webhook update.',
    CONFIRM_WEBHOOK_UPDATED_BUTTON_TEXT: 'Confirm',
    TEMPLATES_TAB_TITLE: 'Templates',
    GROUPS_TAB_TITLE: 'Groups',
    BROADCAST_TAB_TITLE: 'Broadcast',
  };
  
  /**
   * WhatsApp Business Manager Setup Steps
   * Step-by-step instructions for webhook configuration
   */
  export const WhatsAppSetupSteps = {
    WHATSAPP_BUSINESS_MANAGER_STEP_1: 'Go to App Settings → Webhooks',
    WHATSAPP_BUSINESS_MANAGER_STEP_2: 'Click "Configure Webhooks',
    WHATSAPP_BUSINESS_MANAGER_STEP_3: 'Paste the Webhook URL in the "Callback URL" field',
    WHATSAPP_BUSINESS_MANAGER_STEP_4: 'Paste the Verify Token in the "Verify Token" field',
    WHATSAPP_BUSINESS_MANAGER_STEP_5: 'Select subscription fields: messages, message_deliveries, message_reads',
    WHATSAPP_BUSINESS_MANAGER_STEP_6: 'Click "Verify and Save"'
  };
  
  /**
   * WhatsApp Mock Data
   * Mock webhook credentials for testing
   */
  export const WhatsAppMockData = {
    WEBHOOK_URL: 'https://api.yourbusiness.com/webhooks/whatsapp/leads/abc123def456',
    VERIFY_TOKEN: 'whatsapp_verify_token_xyz789abc123def456'
  };
  
  /**
   * Helper function to check if a connection status is valid
   * @param {string} status - The status to validate
   * @returns {boolean} - True if the status is valid
   */
  export const isValidWhatsAppConnectionStatus = (status) => {
    return Object.values(WhatsAppConnectionStatus).includes(status);
  };
  
  /**
   * Helper function to check if an OAuth status is valid
   * @param {string} status - The status to validate
   * @returns {boolean} - True if the status is valid
   */
  export const isValidWhatsAppOAuthStatus = (status) => {
    return Object.values(WhatsAppOAuthStatus).includes(status);
  };
  
  /**
   * Helper function to get the display text for a connection status
   * @param {string} status - The connection status
   * @returns {string} - Display text for the status
   */
  export const getConnectionStatusText = (status) => {
    const statusTexts = {
      [WhatsAppConnectionStatus.CHECKING]: 'Checking connection...',
      [WhatsAppConnectionStatus.CONNECTED]: 'Connected',
      [WhatsAppConnectionStatus.DISCONNECTED]: 'Disconnected',
      [WhatsAppConnectionStatus.NOT_CONNECTED]: 'Not Connected'
    };
    return statusTexts[status] || 'Unknown Status';
  };

  //WhatsApp message status constants
  export const WhatsAppMsgStatus = {
    Sent: 1,
    Delivered: 2,
    Read: 3,
    Received: 4,
    Failed: 5
  };

export const WhatsAppTemplateCategory = {
  MARKETING: 1,
  UTILITY: 2,
  AUTHENTICATION: 3
};

export const WhatsAppTemplateStatus = {
  PENDING: 1,
  APPROVED: 2,
  REJECTED: 3,
  PAUSED: 4,
  DISABLED: 5
};

export const WhatsAppTemplateHeaderType = {
  TEXT: 1,
  IMAGE: 2,
  VIDEO: 3,
  DOCUMENT: 4
};

export const BroadcastStatus = {
  Pending: 0,
  Running: 1,
  Completed: 2,
  PartiallyFailed: 3,
  Failed: 4
};
